import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { NgxsModule } from '@ngxs/store';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { LayoutModule } from '../../layout/layout.module';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ApiInterceptor } from '../../interceptors/api.interceptor';
import { BookmarksModule } from '../../bookmarks/bookmarks.module';
import { EngineModule } from '../../engine/engine.module';
import { StoryModule } from '../../story/story.module';
import { OrganizationsModule } from '../../organizations/organizations.module';

import { DialogModule } from '../../dialogs/dialog.module';
import { AuthModule } from '../../auth/auth.module';
import { LocaleConfigModule } from '../../locale-config.module';
import { NotFoundComponent } from '../../pages/not-found/not-found.component';
import { environment } from 'src/environments/environment';
import { UrlSerializer } from '@angular/router';
import { CustomUrlSerializer } from 'src/app/shared/libraries/url/custom-url-serializer';
import { BreakpointModule } from 'src/app/breakpoint/breakpoint.module';
import { UndoModule } from 'src/app/undo/undo.module';
import { EnvironmentApplication } from 'src/environments/models/environment-application';
import { MemoryModule } from 'src/app/memory/memory.module';
import { MarkdownConfigModule } from 'src/app/markdown-config.module';

import { NgxGoogleAnalyticsModule } from 'ngx-google-analytics';
import { PendoModule } from 'src/app/pendo.module';

import { LinkedinInsightsModule } from 'src/app/linkedin-insights.module';
import { FacebookPixelModule } from 'src/app/facebook-pixel.module';
import { EnvironmentDeploy } from 'src/environments/models/environment-deploy';
import { SharedModule } from 'src/app/shared/shared.module';
import { UserAccessModule } from 'src/app/shared/components/user-access/user-access.module';
import { RECAPTCHA_BASE_URL } from 'ng-recaptcha';
import { AppConfigModule } from '../config/app-config/app-config.module';

declare const immer;
immer.enableAllPlugins();
immer.enableES5();

const isStudio = environment.application === EnvironmentApplication.Studio;

const providers = [
  { provide: HTTP_INTERCEPTORS, useClass: ApiInterceptor, multi: true },
  { provide: UrlSerializer, useClass: CustomUrlSerializer },
  { provide: RECAPTCHA_BASE_URL, useValue: 'https://recaptcha.net/recaptcha/api.js' },
];

@NgModule({
  declarations: [AppComponent, NotFoundComponent],

  imports: [
    // This has to be at the top due to a bug in NgxsOnInit (https://github.com/ngxs/store/issues/539)
    NgxsModule.forRoot(),

    // Google Analytics
    NgxGoogleAnalyticsModule.forRoot(environment.googleAnalyticsId),

    FacebookPixelModule.forRoot({
      enabled: isStudio,
      facebookPixelId: environment.facebookPixelId,
    }),

    LinkedinInsightsModule.forRoot({
      enabled: isStudio,
      linkedinId: environment.linkedinId,
    }),

    // Pendo
    PendoModule.forRoot({
      enabled: isStudio,
      pendoId: environment.pendoId,

      idPrefixes: {
        [EnvironmentDeploy.Local]: 'local-',
        [EnvironmentDeploy.QA]: 'qa-',
        [EnvironmentDeploy.Staging]: 'staging-',
        [EnvironmentDeploy.Production]: '',
      },
    }),

    // Locales
    LocaleConfigModule.forRoot(),

    // Markdown Support
    MarkdownConfigModule,

    // NoopAnimationsModule, // Disable animations for faster UI
    BrowserAnimationsModule,
    BrowserModule,
    HttpClientModule,

    // App dependencies
    AuthModule,
    EngineModule,
    BreakpointModule,

    // Child features
    BookmarksModule,
    LayoutModule,
    OrganizationsModule,
    StoryModule,
    SharedModule,

    UndoModule,

    // Memory tracking (Loft only)
    environment.application === EnvironmentApplication.Loft ? MemoryModule : [],

    // Dialogs
    DialogModule,

    // App routing
    AppRoutingModule,

    NgxsReduxDevtoolsPluginModule.forRoot({
      disabled: environment.production,
    }),

    // User Notifications
    UserAccessModule,

    // App Config
    AppConfigModule,
  ],
  providers: providers,
  bootstrap: [AppComponent],
})
export class AppModule {}
